import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useSubmitSurvivorPick, useSurvivorState } from '@/features/games/survivor';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { SurvivorHistoryEntry, SurvivorOption, SurvivorState } from '@/lib/data/provider';
import { formatKickoffTime, formatNumber } from '@/lib/i18n/format';
import { localizeRound, localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

function OptionRow({
  option,
  selected,
  onPress,
}: {
  option: SurvivorOption;
  selected: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { locale, arabicNumerals } = useLocale();
  const { t } = useTranslation();
  const kickoffRaw = option.kickoff_at ? new Date(option.kickoff_at) : null;
  const kickoff = kickoffRaw && !Number.isNaN(kickoffRaw.getTime()) ? kickoffRaw : null;
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      android_ripple={{ color: 'rgba(255,255,255,0.06)' }}
      className="flex-row items-center gap-3 px-4 py-3 active:opacity-80"
      style={{ backgroundColor: selected ? palette.voltGlass : 'transparent' }}
    >
      <TeamCrest uri={option.logo} name={option.name} size="sm" />
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
          {localizeTeamName({ name: option.name }, locale)}
        </AppText>
        <AppText variant="micro" className="text-muted" numberOfLines={1}>
          {t('survivor.versus', {
            opponent: option.opponent_name ? localizeTeamName({ name: option.opponent_name }, locale) : '',
            time: kickoff ? formatKickoffTime(kickoff, { locale, arabicNumerals }) : '—',
          })}
        </AppText>
      </View>
      <Ionicons
        name={selected ? 'checkmark-circle' : 'chevron-forward'}
        size={selected ? 18 : 16}
        color={selected ? palette.volt : palette.muted}
      />
    </Pressable>
  );
}

function HistoryRow({ entry }: { entry: SurvivorHistoryEntry }) {
  const palette = useThemePalette();
  const { locale } = useLocale();
  const tone = entry.result === 'win' ? palette.win : entry.result === 'elim' ? palette.loss : palette.muted;
  const icon = entry.result === 'win' ? 'checkmark-circle' : entry.result === 'elim' ? 'close-circle' : 'time-outline';
  return (
    <View className="flex-row items-center gap-3 px-4 py-2.5">
      <TeamCrest uri={entry.team_logo} name={entry.team_name} size="xs" />
      <AppText variant="caption" weight="medium" className="flex-1 text-ink" numberOfLines={1}>
        {localizeTeamName({ name: entry.team_name }, locale)}
      </AppText>
      <AppText variant="micro" className="text-muted" numberOfLines={1}>
        {entry.round_label ? localizeRound(entry.round_label, locale) : ''}
      </AppText>
      <Ionicons name={icon} size={16} color={tone} />
    </View>
  );
}

function StatusBanner({ state }: { state: SurvivorState }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const out = state.status === 'out';
  const joined = state.status !== 'not_joined';
  const tone = out ? palette.loss : joined ? palette.win : palette.voltBright;
  return (
    <Card
      className="mx-4 flex-row items-center gap-3 px-4 py-3.5"
      style={{ borderColor: `${tone}55`, borderWidth: 1 }}
    >
      <View className="h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: `${tone}1F` }}>
        <Ionicons name={out ? 'skull' : joined ? 'shield-checkmark' : 'flag'} size={20} color={tone} />
      </View>
      <View className="flex-1">
        <AppText variant="caption" weight="bold" className="text-ink">
          {out
            ? t('survivor.eliminated', {
                round: state.eliminated_round ? localizeRound(state.eliminated_round, locale) : '',
              })
            : joined
              ? t('survivor.stillAlive')
              : t('survivor.notJoined')}
        </AppText>
        <AppText variant="micro" className="text-soft">
          {t('survivor.roundsSurvived', { count: formatNumber(state.rounds_survived, numeralOptions) })}
        </AppText>
      </View>
    </Card>
  );
}

export default function SurvivorScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { userId } = useAuth();
  const toast = useToast();
  const howto = useHowToPlay('survivor');
  const query = useSurvivorState();
  const submit = useSubmitSurvivorPick();
  const state = query.data;
  const current = state?.current ?? null;
  const myPick = current?.my_pick ?? null;

  const onPick = (teamId: string) => {
    submit.mutate(teamId, { onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }) });
  };

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="survivor" icon="shield-half" />
      <BackHeader
        title={t('survivor.title')}
        right={
          <View className="flex-row items-center gap-2">
            <Pressable
              accessibilityRole="button"
              hitSlop={8}
              onPress={() => router.push('/games/leaderboards-hub')}
              className="active:opacity-70"
            >
              <Ionicons name="podium-outline" size={20} color={palette.voltBright} />
            </Pressable>
            <HowToPlayButton onPress={howto.open} />
          </View>
        }
      />
      <ScrollView contentContainerStyle={{ paddingBottom: 32, gap: 12 }}>
        <View className="mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3">
          <Ionicons name="shield-half" size={20} color={palette.voltBright} />
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('survivor.subtitle')}
            </AppText>
            <AppText variant="micro" className="text-soft">
              {t('survivor.howItWorks')}
            </AppText>
          </View>
        </View>

        {query.isPending ? (
          <View className="gap-3 px-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-64 w-full" />
          </View>
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : !state ? (
          <EmptyState icon="shield-outline" title={t('survivor.noOptionsTitle')} body={t('survivor.noOptionsBody')} />
        ) : (
          <>
            {userId ? <StatusBanner state={state} /> : null}

            {!userId ? (
              <View className="mx-4 rounded-card border border-line bg-surface px-3.5 py-2.5">
                <AppText variant="micro" className="text-soft">
                  {t('predictions.signInToPlay')}
                </AppText>
              </View>
            ) : null}

            {current ? (
              <View className="gap-2">
                <View className="mx-4 flex-row items-center justify-between">
                  <AppText variant="caption" weight="bold" className="uppercase tracking-wide text-soft">
                    {current.round_label ? localizeRound(current.round_label, locale) : t('survivor.thisRound')}
                  </AppText>
                </View>

                {/* The user's current pick */}
                {myPick ? (
                  <Card
                    className="mx-4 flex-row items-center gap-3 px-4 py-3"
                    style={{ borderColor: palette.voltLine, borderWidth: 1 }}
                  >
                    <TeamCrest uri={myPick.team_logo} name={myPick.team_name} size="md" />
                    <View className="flex-1">
                      <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                        {localizeTeamName({ name: myPick.team_name }, locale)}
                      </AppText>
                      <AppText variant="micro" className="text-muted" numberOfLines={1}>
                        {myPick.locked ? t('survivor.locked') : t('survivor.yourPick')}
                      </AppText>
                    </View>
                    {myPick.result ? (
                      <Ionicons
                        name={myPick.result === 'win' ? 'checkmark-circle' : 'close-circle'}
                        size={20}
                        color={myPick.result === 'win' ? palette.win : palette.loss}
                      />
                    ) : (
                      <Ionicons name={myPick.locked ? 'lock-closed' : 'create-outline'} size={16} color={palette.muted} />
                    )}
                  </Card>
                ) : null}

                {/* Options to back (hidden once eliminated) */}
                {state.status !== 'out' && current.options.length > 0 ? (
                  <Card className="mx-4 overflow-hidden">
                    {current.options.map((option, index) => (
                      <View key={option.team_id} className={index > 0 ? 'border-t border-line' : ''}>
                        <OptionRow
                          option={option}
                          selected={myPick?.team_id === option.team_id}
                          onPress={() => onPick(option.team_id)}
                        />
                      </View>
                    ))}
                  </Card>
                ) : state.status !== 'out' && !myPick ? (
                  <EmptyState icon="time-outline" title={t('survivor.noOptionsTitle')} body={t('survivor.noOptionsBody')} />
                ) : null}
              </View>
            ) : (
              <EmptyState icon="shield-outline" title={t('survivor.noOptionsTitle')} body={t('survivor.noOptionsBody')} />
            )}

            {/* History */}
            {state.history.length > 0 ? (
              <View className="gap-2">
                <AppText variant="caption" weight="bold" className="mx-4 uppercase tracking-wide text-soft">
                  {t('survivor.history')}
                </AppText>
                <Card className="mx-4 overflow-hidden">
                  {state.history.map((entry, index) => (
                    <View key={`${entry.round_label}-${index}`} className={index > 0 ? 'border-t border-line' : ''}>
                      <HistoryRow entry={entry} />
                    </View>
                  ))}
                </Card>
              </View>
            ) : null}
          </>
        )}
      </ScrollView>
    </Screen>
  );
}
